"use client";

// WO-FOUNDER-WALK-UNBLOCK-001 · P0 — SIGN-IN DID NOT REACH THE PRICES.
//
// FOUNDER'S REPORT: signed in (Member Preview showing his account), and the transport results still
// rendered BOTH fares side by side — Member $31 / Guest $32, unstruck. The signed-out presentation.
//
// ═══ THE MEASUREMENT, AND IT IS NOT WHAT THE REPORT'S OWN GUESS SAID ═══
// Not "the fare surfaces don't read the session" and not "the results layer never re-renders". Both
// were checked first, on :3021, and both are FINE:
//   • the REACT results (`BookingResults`) read `useCartStore(s => s.session)` and flip live —
//     walked signed-out then signed-in: 20 price boxes → 10, member-only, no reload. Already correct.
//   • the ruled presentation is ALREADY IMPLEMENTED INSIDE THE EXPORT TOO, three times over:
//       `guestBtnDisplay: incl ? "none" : (s.session ? "none" : "flex")`   ← transport, member only
//       `guestStrike:     s.session ? "line-through" : "none"`             ← cards, guest struck
//       `authName: signedIn ? s.session.name : "Guest User"`               ← header account chip
//
// So every rule the founder asked for was already written. **The export keeps its OWN `state.session`,
// in the iframe, and the host has never told it anything.** Worse, hosted it can never set that state
// itself: `doSignIn` correctly REFUSES when `sgc.hosted` (§18 — no faked sessions), so the export's
// session is permanently `null` and every fare, every card and the account chip inside the iframe are
// nailed to the signed-out branch. The founder walks the export on nearly every screen, so "signed in"
// was true in the host and invisible everywhere he looked.
//
// ONE MISSING WIRE, not three broken surfaces. This file is the wire.
//
// ═══ WHY A SEPARATE COMPONENT, AND WHY BOTH DIRECTIONS ═══
// Host → export: pushed on every session change AND whenever the export announces itself, because an
// iframe that has not finished parsing 1.1MB cannot receive a message and there is no load event we
// can trust for "the app inside is listening". The export speaks first (`sg:urlstate`); that is the
// only reliable "I am ready" this channel has, so we answer it.
//
// Export → host: the export's own account menu carries "Log Out". Without a return path, signing out
// there would clear the export and leave the host still holding a JWT — two truths on one screen,
// which is the defect class this whole work order is about. `sg:signout` closes the loop.
//
// Same-origin pinned in both directions, like every other message in this app: the export is served
// from this origin, so `"*"` would hand a customer's session state to whatever framed us.

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { isSignedIn, useCartStore } from "@/lib/cart-store";
import type { Session } from "@/lib/cart-store";
import { trackLogin } from "@/lib/analytics-event";
import { handleDesignCartMessage } from "@/lib/analytics-design-cart";
import { handleDesignAnalyticsMessage } from "@/lib/analytics-design-search";
import { installAccountHandoff } from "@/lib/account-handoff-browser";

interface ExportSession { name: string; email: string; firstName?: string; lastName?: string; phone?: string }

/**
 * What the export's own `state.session` looks like: `{ name, email }` plus whichever verified profile fields the session holds
 * (firstName / lastName / phone) — or null when signed out. Never a JWT.
 */
export function exportSession(session: Pick<Session, "email" | "firstName" | "lastName" | "phone">): ExportSession | null {
  const email = session.email;
  if (!email) return null;
  // The display name is the email's local part. NOT invented: it is the same derivation the export's
  // own sign-in used before it was gated off, and it is the account's own data. A real display name
  // needs a profile the phase-1 contract does not serve (§25.9: no member service exists yet), so a
  // plausible-looking full name would be exactly the fabrication canon forbids.
  // An empty local part (an address starting with "@") falls back to the whole address, never to an empty name.
  const localPart = email.split(/[@]/)[0] ?? "";
  const out: ExportSession = { name: localPart.length > 0 ? localPart : email, email };
  // SG-CHECKOUT-MEMBER-PREFILL-20261019 — the purchaser fields fill from the profile the store verified (/api/me for a bearer
  // session, /api/auth/session for SSO). Only fields it actually holds travel; none is derived from the email.
  for (const k of ["firstName", "lastName", "phone"] as const) {
    const v = session[k];
    if (typeof v === "string" && v.trim()) out[k] = v.trim();
  }
  return out;
}

/**
 * Where a push reaches the design: a SAME-ORIGIN frame if one hosts it, otherwise this window (the design mounts un-framed).
 * SG-CHECKOUT-MEMBER-PREFILL-20261019 — "the first <iframe>" can be Stripe's card field on /booking/review: a push aimed there
 * carries this origin pinned, is dropped, and a late profile would never reach the purchaser fields.
 */
function designTarget(): Window {
  for (const f of Array.from(document.querySelectorAll("iframe"))) {
    try {
      if (f.contentWindow && new URL(f.src, window.location.href).origin === window.location.origin) return f.contentWindow;
    } catch {
      /* an unparseable src is not the design */
    }
  }
  return window;
}

export default function ExportSessionBridge() {
  const email = useCartStore((s) => s.session.email);
  const jwt = useCartStore((s) => s.session.jwt);
  // Signed in by a bearer session OR a server-verified cookie-backed SSO session (no token in the page). The design only ever gets
  // the same-origin, non-secret { name, email } for either — never a JWT, in any mode.
  const signed = useCartStore((s) => isSignedIn(s.session));
  const firstName = useCartStore((s) => s.session.firstName);
  const lastName = useCartStore((s) => s.session.lastName);
  const phone = useCartStore((s) => s.session.phone);
  const signOut = useCartStore((s) => s.signOut);
  const loadProfile = useCartStore((s) => s.loadProfile);
  // Read in the listener without making it a dependency: re-binding the listener on every session
  // change would drop messages that arrive during the swap.
  const latest = useRef<ExportSession | null>(null);
  latest.current = signed ? exportSession({ email, firstName, lastName, phone }) : null;

  useEffect(() => {
    if (typeof window === "undefined") return;

    // FOUNDER-WALK-LIVE-RECONCILE-023 · S1 — THE EXPORT IS NO LONGER FRAMED. Since FOUNDER-WALK-QA-STAGING-007
    // the design mounts un-framed in this same document, so a push aimed only at an <iframe> reached
    // nobody: measured on staging bd11c506, a host sign-in on /cart left the design's account menu
    // reading "Not signed in". The un-framed export listens on this window (same origin check on both
    // sides), so the page itself is the target when no frame exists.
    const push = () => {
      designTarget().postMessage(
        { type: "sg:session", session: latest.current },
        window.location.origin,
      );
    };

    const onMessage = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const d = ev.data as { type?: string } | null;
      if (!d?.type) return;
      // The export announcing its state IS its readiness signal — answer it with who is signed in.
      if (d.type === "sg:urlstate") push();
      // Signed out from inside the export: clear the host session so the two cannot disagree.
      if (d.type === "sg:signout" && useCartStore.getState().session.jwt) signOut();
      // SSO mode: the same sign-out for a cookie-backed session (signOut also clears the cookie and ends the issuer session).
      if (d.type === "sg:signout" && useCartStore.getState().session.cookie) signOut();
      // A real add/remove in the design's own cart: reported through the host-gated GA4 helper (production host + consent).
      if (d.type === "sg:cart") handleDesignCartMessage(ev.data);
      // The design's own Search / product Details open: GA4 search or view_item through the same gated helper.
      if (d.type === "sg:ga") handleDesignAnalyticsMessage(ev.data);
      // FOUNDER-WALK-LIVE-RECONCILE-023 · S1 — signed in from inside the export (its sheet posted to the
      // host's /api/auth/login and got a real JWT): adopt that server-issued session so the persisted
      // host store, the account chrome and the next page load all agree. Nothing is written that the
      // proxy did not issue; a message without a jwt is ignored.
      if (d.type === "sg:signin") {
        const m = d as { jwt?: unknown; email?: unknown };
        // SSO mode: a JWT is never adopted from a posted message — the session is the server-verified cookie only.
        if (useCartStore.getState().sso === true) return;
        if (typeof m.jwt === "string" && m.jwt && typeof m.email === "string" && m.email) {
          // The sheet posts this only after the server issued the session, so it is a real sign-in — but
          // the same JWT re-announced (a re-mount, a second listener) is not another one.
          const fresh = useCartStore.getState().session.jwt !== m.jwt;
          // Re-announced, the session (and the profile loaded for it) is kept as it is.
          useCartStore.setState(fresh ? { session: { jwt: m.jwt, email: m.email }, lastError: null } : { lastError: null });
          if (fresh) trackLogin("email");
        }
      }
    };

    // The un-framed design announces cart lines with a synchronous same-document CustomEvent (postMessage to its own
    // window is async and can be lost when a transport pick navigates right after). Framed, `sg:cart` arrives above.
    const onCartEvent = (ev: Event) => handleDesignCartMessage((ev as CustomEvent).detail);
    const onGaEvent = (ev: Event) => handleDesignAnalyticsMessage((ev as CustomEvent).detail);

    window.addEventListener("message", onMessage);
    window.addEventListener("sg:cart", onCartEvent);
    window.addEventListener("sg:ga", onGaEvent);
    // Belt for the case where the export mounted and broadcast before this listener existed (a warm
    // bfcache restore, a fast static page). Three cheap attempts, then stop — the `sg:urlstate`
    // answer above is the real mechanism and a permanent poll would be a leak, not a safety net.
    const timers = [80, 400, 1200].map((ms) => window.setTimeout(push, ms));
    push();

    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("sg:cart", onCartEvent);
      window.removeEventListener("sg:ga", onGaEvent);
      timers.forEach(window.clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── QA-BATCH-2 · item 19 — THE SHARED COOKIE IS KEPT ALIVE WHILE THE SESSION IS ──────────────────
  // The page's JWT lives in localStorage without an expiry; the `sb_access` cookie the account app
  // verifies is written for 24h. After that the header says "signed in" and every Profile / My Orders /
  // Wallet row bounced to accounts/login?next=… (see src/lib/session-refresh.ts). So whenever this page
  // holds a JWT, the cookie is re-issued from it through POST /api/auth/session — which re-writes it only
  // if Strapi still accepts the token, and clears it if not. Once per JWT per document; fire-and-forget.
  const refreshedFor = useRef<string | null>(null);
  useEffect(() => {
    if (typeof window === "undefined" || !jwt || refreshedFor.current === jwt) return;
    refreshedFor.current = jwt;
    void fetch("/api/auth/session", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jwt }),
    })
      .then((res) => {
        // 401 = the server no longer accepts this JWT: the local session must not outlive it either,
        // or the chrome keeps promising an account the next hop will refuse.
        if (res.status === 401) signOut();
      })
      .catch(() => { /* network: the cookie stays as it was; nothing is invented */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt]);

  // SNOWCLUB-V101-PROD-ACCOUNT-HANDOFF-20260927-001 — cross-domain accounts links carry this session over
  // the /bridge popup (see src/lib/account-handoff.ts). Torn down with the component: listener, timers, popup.
  useEffect(() => installAccountHandoff(), []);

  // SG-CHECKOUT-MEMBER-PREFILL-20261019 — on the booking pages, a bearer session's profile is loaded by the store's own
  // loadProfile (the same /api/me read the React funnel's usePrefill uses), once per JWT per document and only while it has never
  // been loaded (`undefined`; a loaded profile without a field is `null`). A cookie-backed SSO session already carries its
  // verified profile.
  const pathname = usePathname();
  const onBooking = (pathname ?? "").includes("/booking/");
  const profiledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!jwt || !onBooking || profiledFor.current === jwt || useCartStore.getState().session.firstName !== undefined) return;
    profiledFor.current = jwt;
    void loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt, onBooking]);

  // The push that makes it flip WITHOUT A RELOAD, in both directions: this effect re-runs whenever the
  // session changes, which is what turning Member Preview on or off does — and when a verified profile
  // arrives after the email, so the purchaser fields can fill from it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    designTarget().postMessage(
      { type: "sg:session", session: signed ? exportSession({ email, firstName, lastName, phone }) : null },
      window.location.origin,
    );
  }, [signed, email, firstName, lastName, phone]);

  return null;
}
